import textBoxData from "./TextBoxConfig";
import { TextBoxEditor } from "./TextBoxEditor";
import { TextBoxModal } from "./TextBoxModal";
import TextBoxDataValidator from "./TextBoxDataValidator"; // Correct integration

import debug from "debug";
import TextBoxGeom from "./TextBoxGeom";
import TextBoxUtils from "./TextBoxUtils";
import TextBoxMethods from "./TextBoxMethods";

const log = debug("app:txt");

/**
 * Manages text boxes in a 3D scene, handling creation, editing, validation, and interaction events.
 * @category Components
 * @subcategory TextBox
 */
export default class TextBoxManager {
  /**
   * Creates an instance of TextBoxManager.
   * @param {Object} experience - The main experience object containing scene and event hub.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.cssGroup = null; // Unified object for both modal and editor
    this.scale = 50;
    this.isEditing = false; // Explicit edit mode state
    this.timeout = null; // Debounce timeout
    this.dataValidator = new TextBoxDataValidator(textBoxData);
    this.textBoxModal = TextBoxModal.getInstance(this.experience);
    this.textBoxEditor = TextBoxEditor.getInstance(this.experience);
    this.textBoxUtils = new TextBoxUtils(this.experience);
    this.textBoxMethods = new TextBoxMethods();

    this.initEventListeners();
  }

  /**
   * Initializes event listeners for various interactions with text boxes.
   */
  initEventListeners() {
    this.createCSSContainer();
    this.eventHub.on("textBox", (data) => this.onTextBoxEvent(data));
    this.eventHub.on("rightClickTextBox", (textBoxMesh) =>
      this.onStartEditTextBox(textBoxMesh)
    );
    this.eventHub.on("doubleClickTextBox", (textBoxMesh) =>
      this.onStartEditTextBox(textBoxMesh)
    );
    this.eventHub.on("exitEditTextBox", () => this.onExitEditTextBox());

    this.eventHub.on("selectionUpdated", ({ selectedObjects }) =>
      this.onSelectionUpdated(selectedObjects)
    );

    this.eventHub.on("cameraYDirectionChanged", (cameraYDirection) =>
      this.flipTextBoxes(cameraYDirection)
    );

    this.eventHub.on(
      "editChanged",
      ({ objectId, mode, previousState, changedState }) => {
        if (mode === "textbox") {
          this.updateTextBoxStyles(objectId, changedState);
        }
      }
    );
  }

  cleanup() {
    console.log("TextBoxManager cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.eventHub) {
      this.eventHub.off("textBox", this.onTextBoxEvent);
      this.eventHub.off("rightClickTextBox", this.onStartEditTextBox);
      this.eventHub.off("doubleClickTextBox", this.onStartEditTextBox);
      this.eventHub.off("exitEditTextBox", this.onExitEditTextBox);
      this.eventHub.off("selectionUpdated", this.onSelectionUpdated);
      this.eventHub.off("cameraYDirectionChanged", this.flipTextBoxes);
      this.eventHub.off("editChanged", this.updateTextBoxStyles);
    }
  
    // 2. Remove dynamically added selection event listeners
    this.selectEventListeners("off");
  
    // 3. Cleanup child instances
    if (this.textBoxEditor) {
      this.textBoxEditor.cleanup();
    }
    if (this.textBoxModal) {
      this.textBoxModal.cleanup();
    }
  
    // 4. Clear stored references
    this.cssGroup = null;
    this.isEditing = false;
    this.timeout = null;
  
    console.log("TextBoxManager cleanup completed.");
  }

  /**
   * Handles the creation of a text box based on input data.
   * @param {Object} data - Data for configuring the text box.
   */
  onTextBoxEvent(data) {
    log("textboxdata", data);
    const validatedData = this.dataValidator.validate(data);
    log("textboxvalidateddata", validatedData);

    const textBoxGeom = new TextBoxGeom(this.experience);
    textBoxGeom.createTextBoxObject(validatedData, this.scale);
  }

  /**
   * Debounces a function to prevent excessive calls within a short time.
   * @param {Function} func - The function to debounce.
   * @param {number} delay - The delay in milliseconds.
   */
  debounce(func, delay) {
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(func, delay);
  }

  /**
   * Starts the editing mode for a text box.
   * @param {Object} textBoxMesh - The mesh object representing the text box.
   */
  onStartEditTextBox(textBoxMesh) {

    log("this is textboxmesh", textBoxMesh)
    this.debounce(() => {
      if (this.isEditing || !textBoxMesh) return;
      this.isEditing = true;
      const cssGroup = textBoxMesh.mesh;
      log("this. is the cssGroup", cssGroup)
      this.eventHub.emit("editTextBox", cssGroup, this.scale);
      //this.selectEventListeners("off");
      this.selectEventListeners("on", cssGroup);
    }, 150);
  }

  /**
   * Exits the editing mode for a text box.
   */
  onExitEditTextBox() {
    if (this.isEditing) {
      this.isEditing = false;
      this.selectEventListeners("off");
      log("exitedittextbox");
    }
  }

  /**
   * Updates the selection of text boxes in the scene.
   * @param {Array<Object>} selectedObjects - The currently selected objects.
   */
  onSelectionUpdated(selectedObjects) {
    log("selectedObjects", selectedObjects);
    const matchingTextBoxes = selectedObjects.filter(
      (obj) => obj.name === "XMO" && obj.userData.mode === "textbox"
    );

    this.eventHub.emit("selectedTextBoxes", matchingTextBoxes);

    if (matchingTextBoxes.length === 0) {
      this.emitExitEditTextBox();
    }
  }

  /**
   * Creates a container for CSS3D objects in the DOM.
   * @returns {HTMLDivElement} The created container element.
   */
  createCSSContainer() {
    const cssContainer = document.createElement("div");
    cssContainer.id = "css3d-container";
    cssContainer.style.position = "absolute";
    cssContainer.style.top = "0";
    cssContainer.style.left = "0";
    cssContainer.style.width = "100%";
    cssContainer.style.height = "100%";
    cssContainer.style.zIndex = "0"; // Layer control above WebGL canvas but below other UI
    cssContainer.style.pointerEvents = "none";
    document.body.appendChild(cssContainer);
    return cssContainer;
  }

  /**
   * Flips the text boxes based on the camera's Y direction.
   * @param {number} cameraYDirection - The camera's Y-axis direction.
   */
  flipTextBoxes(cameraYDirection) {
    log("Flipping text boxes based on cameraYDirection:", cameraYDirection);
    this.experience.scene.traverse((object) => {
      if (object.name === "XMO" && object.userData.mode === "textbox") {
        log("Flipping object:", object);
        object.rotation.x = cameraYDirection > 0 ? 0 : Math.PI;
        object.position.y = cameraYDirection > 0 ? 1 : -1;
      }
    });
  }

  /**
   * Updates the styles of a text box based on its changed state.
   * @param {string} objectId - The ID of the text box.
   * @param {Object} changedState - The new state of the text box.
   */
  updateTextBoxStyles(objectId, changedState) {
    this.experience.scene.traverse((object) => {
      if (
        object.userData?.objectId === objectId &&
        object.userData.mode === "textbox"
      ) {
        const cssObject = object.getObjectByName("CSS3DObject");
        if (cssObject) {
          const textBoxElement = cssObject.element;
          this.textBoxMethods.applyStyles(
            textBoxElement,
            changedState,
            this.scale
          );
          Object.assign(object.userData, changedState);
          const { w, h } = changedState;
          this.textBoxEditor.updateTextBoxSize({
            cssGroup: object,
            scale: this.scale,
            newWidth: w * this.scale,
            newHeight: h * this.scale,
          });
        }
      }
    });
  }

  /**
   * Manages event listeners for text box selection.
   * @param {string} action - Either "on" or "off" to add or remove listeners.
   * @param {Object|null} cssGroup - The CSS group to manage listeners for.
   */
  selectEventListeners(action, cssGroup = null) {
    const manageListener = (event, handler) =>
      this.eventHub[action === "on" ? "on" : "off"](event, handler);

    if (action === "on" && this.textBoxEditor.cssGroup !== cssGroup) {
      this.emitExitEditTextBox();
      return;
    }

    this.boundClearAllHandler = (event) => this.handleClearAll(event);
    this.boundMultiSelectHandler = () => this.emitExitEditTextBox();
    this.boundModifierSelectHandler = () => this.emitExitEditTextBox();
    this.boundSingleSelectHandler = ({ selectedObjects }) =>
      this.handleSingleSelect(selectedObjects || []);

    manageListener("clearAll", this.boundClearAllHandler);
    manageListener("multiSelect", this.boundMultiSelectHandler);
    manageListener("modifierSelect", this.boundModifierSelectHandler);
    manageListener("singleSelect", this.boundSingleSelectHandler);
  }

  /**
   * Handles clearing all selected text boxes.
   * @param {MouseEvent} event - The mouse event triggering the action.
   */
  handleClearAll(event) {
    const pointerX = event?.clientX ?? 0;
    const pointerY = event?.clientY ?? 0;

    const css3DObject =
      this.textBoxEditor?.cssGroup?.getObjectByName("CSS3DObject");
    const isWithinTextbox = css3DObject?.element
      ? this.isMouseWithinElement(css3DObject.element, pointerX, pointerY)
      : false;

    const isWithinToolbox = this.textBoxModal?.toolbarContainer
      ? this.isMouseWithinElement(
          this.textBoxModal.toolbarContainer,
          pointerX,
          pointerY
        )
      : false;

    if (!isWithinTextbox && !isWithinToolbox) {
      this.emitExitEditTextBox();
    }
  }

  /**
   * Checks if the mouse is within the bounds of a given DOM element.
   * @param {HTMLElement} element - The DOM element to check against.
   * @param {number} pointerX - The X-coordinate of the mouse pointer.
   * @param {number} pointerY - The Y-coordinate of the mouse pointer.
   * @returns {boolean} Whether the mouse is within the element.
   */
  isMouseWithinElement(element, pointerX, pointerY) {
    if (!element) return false;
    try {
      const rect = element.getBoundingClientRect();
      return (
        pointerX >= rect.left &&
        pointerX <= rect.right &&
        pointerY >= rect.top &&
        pointerY <= rect.bottom
      );
    } catch {
      return false;
    }
  }

  /**
   * Handles single selection of text boxes.
   * @param {Array<Object>} selectedObjects - The currently selected objects.
   */
  handleSingleSelect(selectedObjects) {
    if (!Array.isArray(selectedObjects)) {
      log("Invalid selectedObjects:", selectedObjects);
      this.emitExitEditTextBox();
      return;
    }
    if (
      selectedObjects.length !== 1 ||
      selectedObjects[0] !== this.textBoxEditor.cssGroup
    ) {
      this.emitExitEditTextBox();
    }
  }

  /**
   * Emits an event to exit text box editing mode.
   */
  emitExitEditTextBox() {
    if (this.isEditing) {
      this.isEditing = false;
      this.eventHub.emit("exitEditTextBox");
    }
  }
}
